import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useDemo } from "@/contexts/DemoContext";
import { Loader2 } from "lucide-react";
import PendingApproval from "@/pages/PendingApproval";

interface ProtectedRouteProps {
  children: ReactNode;
  requireCompany?: boolean;
}

// Routes subcontractors are NOT allowed to access
const RESTRICTED_FOR_SUBS = [
  "/leads", "/estimates", "/invoices", "/calendar", "/inbox",
  "/stakeholders", "/team", "/reports", "/email-marketing",
  "/sms", "/tools", "/settings", "/integrations",
];

export function ProtectedRoute({ children, requireCompany = true }: ProtectedRouteProps) {
  const { user, isLoading: authLoading } = useAuth();
  const { hasCompany, isLoading: companyLoading, hasPendingRequest, pendingCompanyName } = useCompany();
  const { isSubcontractor } = usePermissions();
  const { isDemoMode } = useDemo();
  const location = useLocation();

  if (authLoading || (user && companyLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (requireCompany && !companyLoading && !hasCompany && !isDemoMode) {
    return <PendingApproval companyName={pendingCompanyName || undefined} />;
  }

  // Block subcontractors from restricted routes
  if (isSubcontractor && RESTRICTED_FOR_SUBS.some((r) => location.pathname === r || location.pathname.startsWith(r + "/"))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
